<template>
    <div class="py-10">
        <header>
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h2 class="py-0.5 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:leading-9 sm:truncate">
                            {{ $t('Ticket details') }}
                        </h2>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <router-link
                            class="btn btn-blue shadow-sm rounded-md"
                            to="/tickets/list"
                        >
                            {{ $t('Return to tickets list') }}
                        </router-link>
                    </div>
                </div>
            </div>
        </header>
        <main>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-10 my-6 bg-white shadow overflow-hidden sm:rounded-md">
                    <loading :status="loading.form"/>
                    <div class="sm:flex sm:items-center py-3 max-w-full">
                        <div class="px-6 sm:pl-6 sm:pr-3 sm:flex-1 sm:w-3/4">
                            <div class="text-xl truncate">{{ ticket.subject }}</div>
                        </div>
                        <div class="px-6 sm:pl-3 sm:pr-6 sm:flex-1 sm:w-1/4">
                            <div class="flex items-center sm:float-right">
                                <div class="text-sm sm:pr-2">{{ ticket.created_at | momentFormatDateTimeAgo }}</div>
                                <button class="flex items-center btn btn-white p-2 ml-3 sm:ml-0" type="button" @click="replyForm = true">
                                    <svg-vue class="h-4 w-4 mr-2" icon="font-awesome.reply-regular"></svg-vue>
                                    {{ $t('Reply') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="ticket.approval_required"
                        :class="approvalFlowClass"
                        class="mx-6 mb-3 p-3 rounded border text-sm"
                    >
                        <div class="flex items-center justify-between">
                            <div class="font-semibold">{{ $t('Approval flow') }}</div>
                            <span :class="approvalStatusBadgeClass" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold">
                                {{ approvalStatusLabel }}
                            </span>
                        </div>
                        <div v-if="approvalPending" class="mt-1">
                            <div class="font-medium">{{ $t('Waiting approval') }}</div>
                            <div class="mt-2">
                                <span>{{ $t('Approvers') }}:</span>
                                <span class="font-medium">{{ approverNames }}</span>
                            </div>
                        </div>
                        <div v-else-if="ticket.approval_status === 'approved'" class="mt-1">
                            <span>{{ $t('Approved by') }}:</span>
                            <span class="font-medium">{{ ticket.approved_by_user ? ticket.approved_by_user.name : '-' }}</span>
                        </div>
                        <div v-else-if="ticket.approval_status === 'rejected'" class="mt-1">
                            <span>{{ $t('Rejected by') }}:</span>
                            <span class="font-medium">{{ ticket.rejected_by_user ? ticket.rejected_by_user.name : '-' }}</span>
                        </div>
                        <div v-else-if="ticket.approval_status === 'canceled'" class="mt-1">
                            <span>{{ $t('Canceled by') }}:</span>
                            <span class="font-medium">{{ ticket.canceled_by_user ? ticket.canceled_by_user.name : '-' }}</span>
                        </div>
                    </div>
                    <div v-if="replyForm" class="px-6 py-3 border-t">
                        <loading :status="loading.reply"/>
                        <form @submit.prevent="addReply">
                            <input-wysiwyg
                                id="ticket_body"
                                v-model="ticketReply.body"
                                :plugins="{images: true, attachment: true, shortCode: true}"
                                @selectUploadFile="selectUploadFile"
                            >
                                <template v-slot:top>
                                    <div :class="{'bg-gray-200': uploadingFileProgress > 0}" class="h-1 w-full">
                                        <div :style="{width: uploadingFileProgress + '%'}" class="bg-blue-500 py-0.5"></div>
                                    </div>
                                </template>
                                <template v-slot:bottom>
                                    <div class="flex justify-between border border-t-0">
                                        <button
                                            class="btn btn-secondary rounded-none"
                                            type="button"
                                            @click="discardReply"
                                        >
                                            {{ $t('Discard') }}
                                        </button>
                                        <div class="flex">
                                            <select
                                                id="status"
                                                v-model="ticketReply.status_id"
                                                :disabled="!canChangeStatus"
                                                aria-label="Status"
                                                class="block form-select pl-3 pr-9 py-2 border-l border-r-0 border-t-0 border-b-0 border-gray-400 rounded-none bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
                                            >
                                                <template v-for="status in statusList">
                                                    <option :value="status.id">{{ status.name }}</option>
                                                </template>
                                            </select>
                                            <button
                                                class="btn btn-green rounded-none"
                                                type="submit"
                                            >
                                                {{ $t('Send reply') }}
                                            </button>
                                        </div>
                                    </div>
                                    <div v-if="!canChangeStatus" class="text-xs text-yellow-700 px-3 py-2 border-t">
                                        {{ statusChangeHint }}
                                    </div>
                                </template>
                            </input-wysiwyg>
                        </form>
                        <input ref="fileInput" hidden type="file" @change="uploadFile($event)">
                        <template v-if="ticketReply.attachments.length > 0">
                            <div class="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                                <template v-for="(attachment, index) in ticketReply.attachments">
                                    <attachment :details="attachment" v-on:remove="removeAttachment(index)"/>
                                </template>
                            </div>
                        </template>
                    </div>
                    <div v-if="hasNewIncomingReply" class="px-6 pb-2">
                        <button
                            class="inline-flex items-center px-3 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-medium"
                            type="button"
                            @click="acknowledgeNewReplies"
                        >
                            {{ $t('New message') }}
                        </button>
                    </div>
                    <template v-if="ticket.ticketReplies.length > 0">
                        <template v-for="ticketReply in ticket.ticketReplies">
                            <div :class="isOwnReply(ticketReply) ? 'justify-end' : 'justify-start'" class="ticket-reply-item flex p-6 border-t">
                                <div :class="isOwnReply(ticketReply) ? 'bg-blue-50 border-blue-100' : 'bg-gray-50 border-gray-200'" class="w-full max-w-3xl border rounded-2xl p-4 shadow-sm">
                                    <div class="md:flex md:items-center pb-1">
                                        <div class="md:flex-1 text-lg font-semibold text-gray-800 flex items-center flex-wrap">
                                            <span>{{ ticketReply.user.name }}</span>
                                            <template v-for="role in ticketReply.sender_roles || []">
                                                <span :class="roleBadgeClass(role)" class="ml-2 px-2 py-0.5 rounded text-xs font-semibold">
                                                    {{ roleBadgeLabel(role) }}
                                                </span>
                                            </template>
                                        </div>
                                        <div class="md:flex-1">
                                            <div class="md:float-right text-sm flex items-center">
                                                {{ ticketReply.created_at | momentFormatDateTime }}
                                                <span v-if="ticketReply.is_edited" class="ml-2 text-xs text-gray-500">{{ $t('Edited') }}</span>
                                                <button
                                                    v-if="ticketReply.can_edit"
                                                    class="ml-2 text-xs text-blue-600 hover:text-blue-800 focus:outline-none"
                                                    type="button"
                                                    @click="startEditReply(ticketReply)"
                                                >
                                                    {{ $t('Edit') }}
                                                </button>
                                                <button
                                                    v-if="isOwnReply(ticketReply)"
                                                    :title="$t('See who has read this message')"
                                                    class="ml-2 focus:outline-none"
                                                    type="button"
                                                    @click="toggleSeenBy(ticketReply.id)"
                                                >
                                                    <span v-if="ticketReply.read_by_count > 0" class="inline-flex items-center text-blue-500">
                                                        <svg-vue class="h-3 w-3 -mr-1" icon="font-awesome.check-circle-solid"></svg-vue>
                                                        <svg-vue class="h-3 w-3" icon="font-awesome.check-circle-solid"></svg-vue>
                                                    </span>
                                                    <span v-else class="inline-flex items-center text-gray-400">
                                                        <svg-vue class="h-3 w-3" icon="font-awesome.check-circle-light"></svg-vue>
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="editingReplyId === ticketReply.id" class="mt-2">
                                        <textarea
                                            v-model="editingReplyBody"
                                            class="w-full form-textarea"
                                            rows="5"
                                        ></textarea>
                                        <div class="mt-2 flex justify-end">
                                            <button class="btn btn-white mr-2" type="button" @click="cancelEditReply">
                                                {{ $t('Cancel') }}
                                            </button>
                                            <button :disabled="loading.editReply" class="btn btn-blue" type="button" @click="updateReply(ticketReply)">
                                                {{ $t('Save') }}
                                            </button>
                                        </div>
                                    </div>
                                    <p v-else class="text-gray-700 ticket-reply-body" v-html="ticketReply.body"/>
                                    <div v-if="ticketReply.status_change" class="mt-2 inline-flex items-center text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 px-3 py-1">
                                        {{ $t('Status changed') }}: {{ ticketReply.status_change.from.name }} -> {{ ticketReply.status_change.to.name }}
                                    </div>
                                    <div v-if="seenByOpen[ticketReply.id]" class="mt-2 rounded-lg border border-gray-200 bg-white p-2">
                                        <div class="text-xs font-semibold text-gray-600 mb-1">{{ $t('Read by') }}</div>
                                        <template v-if="ticketReply.read_by_users && ticketReply.read_by_users.length > 0">
                                            <div v-for="reader in ticketReply.read_by_users" :key="reader.id" class="flex items-center justify-between text-xs text-gray-600 py-1">
                                                <div class="flex items-center">
                                                    <img
                                                        :alt="$t('Avatar')"
                                                        :src="reader.avatar !== 'gravatar' ? reader.avatar : reader.gravatar"
                                                        class="h-5 w-5 rounded-full mr-2 object-cover"
                                                    />
                                                    <span>{{ reader.name }}</span>
                                                </div>
                                                <span>{{ formatReadAt(reader.read_at) }}</span>
                                            </div>
                                        </template>
                                        <div v-else class="text-xs text-gray-500 py-1">{{ $t('No one has read this message yet') }}</div>
                                    </div>
                                    <template v-if="ticketReply.attachments.length > 0">
                                        <div class="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                                            <template v-for="attachment in ticketReply.attachments">
                                                <attachment :details="attachment" :remove-button="false"/>
                                            </template>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </template>
                    </template>
                    <template v-else>
                        <div class="h-full flex border-t">
                            <div class="m-auto">
                                <div class="grid grid-cols-1 justify-items-center h-full w-full py-24">
                                    <div class="flex justify-center items-center">
                                        <svg-vue class="h-full h-auto w-48 mb-6" icon="undraw.task-list"></svg-vue>
                                    </div>
                                    <div class="flex justify-center items-center">
                                        <div class="w-full font-semibold text-2xl">{{ $t('This ticket has no conversations') }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
export default {
    name: "index",
    metaInfo() {
        return {
            title: this.$i18n.t('Ticket details')
        }
    },
    data() {
        return {
            loading: {
                form: true,
                reply: false,
                file: false,
                editReply: false,
            },
            replyForm: false,
            uploadingFileProgress: 0,
            ticket: {
                subject: null,
                created_at: null,
                approval_required: false,
                approval_status: null,
                approvers: [],
                ticketReplies: [],
            },
            ticketReply: {
                status_id: null,
                body: '',
                attachments: [],
            },
            statusList: [],
            seenByOpen: {},
            realtimeChannel: null,
            realtimeUserChannel: null,
            lastIncomingReplyId: null,
            hasNewIncomingReply: false,
            lastRealtimeReplyId: null,
            realtimeRefreshTimer: null,
            lastRealtimeEventKey: null,
            visibleSyncTimer: null,
            editingReplyId: null,
            editingReplyBody: '',
        }
    },
    filters: {
        momentFormatDateTime: function (value) {
            return moment(value).locale(window.app.app_date_locale).format(window.app.app_date_format + ' HH:mm');
        },
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    mounted() {
        this.getTicket();
        this.getStatuses();
        this.startTicketRealtime();
        this.startVisibleSync();
    },
    beforeDestroy() {
        this.stopVisibleSync();
        this.stopTicketRealtime();
    },
    computed: {
        approvalPending() {
            return this.ticket.approval_required && this.ticket.approval_status === 'pending';
        },
        canChangeStatus() {
            return this.ticket.can_change_status !== false;
        },
        statusChangeHint() {
            if (this.ticket.approval_required && this.ticket.approval_status !== 'approved') {
                return this.$t('Status can be changed only after ticket approval');
            }
            return this.$t('Only ticket owner can change the status');
        },
        approverNames() {
            if (!this.ticket.approvers || this.ticket.approvers.length === 0) {
                return '-';
            }
            return this.ticket.approvers.map((item) => item.user ? item.user.name : '-').join(', ');
        },
        approvalFlowClass() {
            if (this.ticket.approval_status === 'approved') {
                return 'border-green-300 bg-green-50 text-green-900';
            }
            if (this.ticket.approval_status === 'rejected' || this.ticket.approval_status === 'canceled') {
                return 'border-red-300 bg-red-50 text-red-900';
            }
            return 'border-yellow-300 bg-yellow-50 text-yellow-900';
        },
        approvalStatusBadgeClass() {
            if (this.ticket.approval_status === 'approved') {
                return 'bg-green-100 text-green-800';
            }
            if (this.ticket.approval_status === 'rejected' || this.ticket.approval_status === 'canceled') {
                return 'bg-red-100 text-red-800';
            }
            return 'bg-yellow-100 text-yellow-800';
        },
        approvalStatusLabel() {
            if (this.ticket.approval_status === 'approved') {
                return this.$t('Approved');
            }
            if (this.ticket.approval_status === 'rejected') {
                return this.$t('Rejected');
            }
            if (this.ticket.approval_status === 'canceled') {
                return this.$t('Canceled');
            }
            return this.$t('Pending');
        },
    },
    methods: {
        isPageActive() {
            return !document.hidden && document.visibilityState === 'visible' && document.hasFocus();
        },
        getTicket(silent = false) {
            const self = this;
            if (!silent) {
                self.loading.form = true;
            }
            axios.get('api/tickets/' + self.$route.params.uuid, {
                params: {_t: Date.now()}
            }).then(function (response) {
                self.handleTicketResponse(response.data);
            }).catch(function () {
                self.loading.form = false;
                self.$router.push('/tickets/list');
            });
        },
        handleTicketResponse(ticket, eventPayload = null) {
            const latestIncomingReplyId = this.getLatestIncomingReplyId(ticket.ticketReplies || []);
            const eventReplyId = eventPayload && eventPayload.latest_reply_id ? Number(eventPayload.latest_reply_id) : null;
            const eventReplyUserId = eventPayload && eventPayload.latest_reply_user_id ? Number(eventPayload.latest_reply_user_id) : null;
            const isIncomingEvent = eventReplyUserId !== null && this.$store.state.user && eventReplyUserId !== Number(this.$store.state.user.id);
            if (
                eventReplyId !== null
                && isIncomingEvent
                && latestIncomingReplyId !== null
                && latestIncomingReplyId >= eventReplyId
                && this.lastIncomingReplyId !== null
                && eventReplyId > this.lastIncomingReplyId
                && eventReplyId !== this.lastRealtimeReplyId
            ) {
                this.hasNewIncomingReply = true;
                this.lastRealtimeReplyId = eventReplyId;
            }
            this.ticket = ticket;
            this.ticketReply.status_id = ticket.status_id;
            this.lastIncomingReplyId = latestIncomingReplyId;
            this.loading.form = false;
        },
        queueRealtimeRefresh(payload) {
            const eventKey = [
                payload && payload.ticket_uuid ? payload.ticket_uuid : this.$route.params.uuid,
                payload && payload.updated_at ? payload.updated_at : '',
                payload && payload.latest_reply_id ? payload.latest_reply_id : '',
            ].join(':');

            if (eventKey && eventKey === this.lastRealtimeEventKey) {
                return;
            }
            this.lastRealtimeEventKey = eventKey;

            if (this.realtimeRefreshTimer) {
                clearTimeout(this.realtimeRefreshTimer);
            }
            this.realtimeRefreshTimer = setTimeout(() => {
                axios.get('api/tickets/' + this.$route.params.uuid, {
                    params: {_t: Date.now()}
                }).then((response) => {
                    this.handleTicketResponse(response.data, payload);
                }).catch(() => {
                    this.lastRealtimeEventKey = null;
                });
            }, 120);
        },
        getStatuses() {
            const self = this;
            axios.get('api/tickets/statuses').then(function (response) {
                self.statusList = response.data;
            });
        },
        startTicketRealtime() {
            this.stopTicketRealtime();
            if (!window.Echo) {
                return;
            }
            this.realtimeChannel = window.Echo.private('ticket.' + this.$route.params.uuid)
                .listen('.ticket.updated', (payload) => {
                    this.queueRealtimeRefresh(payload);
                });
            if (this.$store.state.user && this.$store.state.user.id) {
                this.realtimeUserChannel = window.Echo.private('users.' + this.$store.state.user.id + '.tickets')
                    .listen('.ticket.updated', (payload) => {
                        if (!payload || payload.ticket_uuid !== this.$route.params.uuid) {
                            return;
                        }
                        this.queueRealtimeRefresh(payload);
                    });
            }
        },
        stopTicketRealtime() {
            if (this.realtimeRefreshTimer) {
                clearTimeout(this.realtimeRefreshTimer);
                this.realtimeRefreshTimer = null;
            }
            this.lastRealtimeEventKey = null;
            if (window.Echo) {
                window.Echo.leave('private-ticket.' + this.$route.params.uuid);
                if (this.$store.state.user && this.$store.state.user.id) {
                    window.Echo.leave('private-users.' + this.$store.state.user.id + '.tickets');
                }
            }
            this.realtimeChannel = null;
            this.realtimeUserChannel = null;
        },
        startVisibleSync() {
            this.stopVisibleSync();
            this.visibleSyncTimer = setInterval(() => {
                if (!this.isPageActive() || this.loading.reply || this.loading.file || this.loading.form) {
                    return;
                }
                this.getTicket(true);
            }, 3000);
        },
        stopVisibleSync() {
            if (this.visibleSyncTimer) {
                clearInterval(this.visibleSyncTimer);
                this.visibleSyncTimer = null;
            }
        },
        acknowledgeNewReplies() {
            this.hasNewIncomingReply = false;
            this.lastIncomingReplyId = this.getLatestIncomingReplyId(this.ticket.ticketReplies || []);
            this.scrollToLatestReply();
        },
        getLatestIncomingReplyId(replies) {
            const incomingReplies = (replies || []).filter((reply) => !this.isOwnReply(reply));
            if (incomingReplies.length < 1) {
                return null;
            }
            return Math.max(...incomingReplies.map((reply) => Number(reply.id) || 0));
        },
        scrollToLatestReply() {
            this.$nextTick(() => {
                const latestReply = this.$el ? this.$el.querySelector('.ticket-reply-item') : null;
                if (latestReply && typeof latestReply.scrollIntoView === 'function') {
                    latestReply.scrollIntoView({behavior: 'smooth', block: 'center'});
                }
            });
        },
        addReply() {
            const self = this;
            self.loading.reply = true;
            axios.post('api/tickets/' + self.$route.params.uuid + '/reply', self.ticketReply).then(function (response) {
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.discardReply();
                self.ticket = response.data.ticket;
                self.ticketReply.status_id = response.data.ticket.status_id;
                self.lastIncomingReplyId = self.getLatestIncomingReplyId(response.data.ticket.ticketReplies || []);
                self.hasNewIncomingReply = false;
                self.lastRealtimeReplyId = null;
                self.loading.reply = false;
            }).catch(function () {
                self.loading.reply = false;
            });
        },
        discardReply() {
            this.ticketReply.body = '';
            this.ticketReply.attachments = [];
            this.replyForm = false;
        },
        selectUploadFile() {
            if (!this.loading.file) {
                this.$refs.fileInput.click();
            } else {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('A file is being uploaded').toString(),
                    type: 'warning'
                });
            }
        },
        uploadFile(e) {
            const self = this;
            const formData = new FormData();
            self.loading.file = true;
            formData.append('file', e.target.files[0]);
            axios.post(
                'api/tickets/attachments',
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: function (progressEvent) {
                        self.uploadingFileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                    }.bind(this)
                }
            ).then(function (response) {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
                self.ticketReply.attachments.push(response.data);
            }).catch(function () {
                self.loading.file = false;
                self.uploadingFileProgress = 0;
                self.$refs.fileInput.value = null;
            });
        },
        removeAttachment(attachment) {
            this.ticketReply.attachments.splice(attachment, 1);
        },
        startEditReply(ticketReply) {
            this.editingReplyId = ticketReply.id;
            this.editingReplyBody = ticketReply.body || '';
        },
        cancelEditReply() {
            this.editingReplyId = null;
            this.editingReplyBody = '';
        },
        updateReply(ticketReply) {
            const self = this;
            self.loading.editReply = true;
            axios.put('api/tickets/' + self.$route.params.uuid + '/reply/' + ticketReply.id, {
                body: self.editingReplyBody,
            }).then(function (response) {
                self.ticket = response.data.ticket;
                self.ticketReply.status_id = response.data.ticket.status_id;
                self.cancelEditReply();
                self.loading.editReply = false;
            }).catch(function () {
                self.loading.editReply = false;
            });
        },
        roleBadgeLabel(role) {
            if (role === 'creator') {
                return this.$t('Creator');
            }
            if (role === 'target') {
                return this.$t('Executor');
            }
            if (role === 'approver') {
                return this.$t('Approver');
            }
            return role;
        },
        roleBadgeClass(role) {
            if (role === 'creator') {
                return 'bg-blue-100 text-blue-800';
            }
            if (role === 'target') {
                return 'bg-green-100 text-green-800';
            }
            if (role === 'approver') {
                return 'bg-purple-100 text-purple-800';
            }
            return 'bg-gray-100 text-gray-800';
        },
        isOwnReply(reply) {
            return !!(this.$store.state.user && reply && reply.user && Number(reply.user.id) === Number(this.$store.state.user.id));
        },
        toggleSeenBy(replyId) {
            this.$set(this.seenByOpen, replyId, !this.seenByOpen[replyId]);
        },
        formatReadAt(value) {
            return moment(value).locale(window.app.app_date_locale).format(window.app.app_date_format + ' HH:mm');
        }
    }
}
</script>
